import { BreadcrumbJsonLd, NewsArticleJsonLd, NextSeo, NextSeoProps } from 'next-seo'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'

import { MakeswiftPageSnapshot } from '@makeswift/runtime/dist/types/next'

import { getBaseUrl } from '@/lib/contentful/utils'

import { getSeoLocale } from '@/utils/locales/getSeoLocale'
import { getLocalizedFromEnglish } from '@/utils/locales/localized-routes/getLocalizedFromEnglish'
import { getNoIndexValueFromMakeSwift } from '@/utils/seo/getNoIndexValueFromMakeSwift'
import { useUrl } from '@/utils/url/useUrl'

type Props = {
	children: React.ReactNode
	languageAlternates: NextSeoProps['languageAlternates']
	snapshot: MakeswiftPageSnapshot
}

export function PressMeta({ children, languageAlternates, snapshot }: Props) {
	const { locale = 'en-US' } = useRouter()
	const url = useUrl({ locale })
	const baseUrl = `${url?.protocol}//${url?.host}`

	const WebsiteLdJson = {
		'@context': 'http://schema.org',
		'@type': 'WebSite',
		author: {
			'@type': 'Corporation',
			name: 'BigCommerce',
			location: { '@type': 'Place', name: 'BigCommerce' }
		},
		copyrightHolder: {
			'@type': 'Corporation',
			name: 'BigCommerce',
			location: { '@type': 'Place', name: 'BigCommerce' }
		},
		copyrightYear: '2021',
		creator: {
			'@type': 'Organization',
			name: 'BigCommerce',
			location: { '@type': 'Place', name: 'BigCommerce' }
		},
		dateModified: '2023-06-21T12:23:44-05:00',
		datePublished: '2021-09-21T13:06:00-05:00',
		description:
			'Recent Bigcommece news, press releases and client testimonials, plus downloadable media kits assets',
		headline: 'Press Center',
		image: {
			'@type': 'ImageObject',
			url: 'https://www-cdn.bigcommerce.com/assets/BigCommerce-Social-Image-Generic-Facebook.jpg'
		},
		inLanguage: 'en-us',
		name: 'BigCommerce'
	}
	const noIndex = getNoIndexValueFromMakeSwift({ snapshot, url })
	return (
		<>
			<Head>
				{languageAlternates?.map(({ hrefLang }) => {
					if (hrefLang === 'x-default') return null

					const alternate = getSeoLocale(hrefLang)

					return <meta name="og:locale:alternate" content={alternate} key={alternate} />
				})}
			</Head>
			<NextSeo
				canonical={`${baseUrl}${getLocalizedFromEnglish(locale as Locale, `/press/`)}`}
				languageAlternates={languageAlternates}
				openGraph={{
					locale: getSeoLocale(locale),
					type: 'website',
					url: `${baseUrl}${getLocalizedFromEnglish(locale as Locale, `/press/`)}`,
					siteName: 'BigCommerce'
				}}
				noindex={noIndex}
			/>

			<NewsArticleJsonLd
				type="NewsArticle"
				url={`${getBaseUrl(locale)}${getLocalizedFromEnglish(locale as Locale, `/press/`)}`}
				publisherName="BigCommerce"
				publisherLogo="https://www-cdn.bigcommerce.com/assets/BigCommerce-Social-Image-Generic-Facebook.jpg"
				images={[
					'https://www-cdn.bigcommerce.com/assets/BigCommerce-Social-Image-Generic-Facebook.jpg'
				]}
				datePublished=""
				dateModified=""
				dateCreated=""
				keywords=""
				description=""
				body=""
				section="Press Center"
				title="Press Center | BigCommerce"
				authorName={{
					type: 'Organization',
					name: 'BigCommerce',
					url: 'https://www.bigcommerce.com/'
				}}
			/>

			<BreadcrumbJsonLd
				itemListElements={[
					{
						position: 1,
						name: 'Homepage',
						item: `${getBaseUrl(locale)}/`
					},
					{
						position: 2,
						name: 'Press Center',
						item: `${getBaseUrl(locale)}${getLocalizedFromEnglish(locale as Locale, `/press/`)}`
					}
				]}
			/>

			<Script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(WebsiteLdJson) }}
				key="entry/ld+json"
				id="entry/ld+json"
				strategy="lazyOnload"
			/>
			{children}
		</>
	)
}
